import React, { useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from './contexts/AuthContext';
import { usePreferences } from './contexts/PreferencesContext';
import { DataProvider } from './contexts/DataContext';
import { AudioProvider } from './contexts/AudioContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/common/ToastContainer';
import AudioPlayer from './components/AudioPlayer';
// Import your components
import { PublicLayout, AppLayout } from './components/layout';
import Dashboard from "./pages/Dashboard";
import DocumentView from "./pages/DocumentView";
import DocumentEdit from "./pages/DocumentEdit";
import Settings from "./pages/settings/Settings";
// SIGN-IN PAGES MOVED
import LoginPage from './pages/signin/LoginPage';
import RegistrationPage from './pages/signin/RegistrationPage';
import PlanSignupPage from './pages/signin/PlanSignupPage';
import SignupSuccessPage from './pages/signin/SignupSuccessPage';
import SignupCanceledPage from './pages/signin/SignupCanceledPage';
import ForgotPasswordPage from './pages/signin/ForgotPasswordPage';
import ResetPasswordPage from './pages/signin/ResetPasswordPage';
import EmailVerificationPage from './pages/signin/EmailVerificationPage';
import LandingPage from './pages/LandingPage';
import CourseView from "./pages/CourseView";
import CourseEdit from "./pages/CourseEdit";
import CourseCardPage from "./pages/CourseCardPage";
import EventCardPage from "./pages/EventCardPage";
import CourseChatPage from "./pages/CourseChatPage";
import CourseDocumentsPage from "./pages/CourseDocumentsPage";
import AssignmentView from "./pages/AssignmentView";
import AssignmentEdit from "./pages/AssignmentEdit";
import EventView from "./pages/EventView";
import EventEdit from "./pages/EventEdit";
import "./App.css";
import "./theme/scholar-enhancements.css"; // Scholar Mode pop enhancements
import { DocumentApi } from './services/ApiService';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import OrgRoleRoute from './components/OrgRoleRoute';
import AnyAdminRoute from './components/AnyAdminRoute';
import CourseAdminDashboard from './pages/courseAdmin/CourseAdminDashboard';
import CourseAdminHome from './pages/courseAdmin/CourseAdminHome';
import OrgAdminDashboard from './pages/orgAdmin/OrgAdminDashboard';
import MyOrganizationsPage from './pages/admin/MyOrganizationsPage';
import ReminderPolicyPage from './pages/admin/ReminderPolicyPage';
import CourseResourcesPage from './pages/CourseResourcesPage';
import CalendarPage from './pages/CalendarPage';
import DocumentsPage from './pages/DocumentsPage';
import AIToolsPage from './pages/AIToolsPage'; // AI Tools hub
import CreateCourseWizard from './pages/createEndeavors/CreateCourseWizard';
import CreateGoalWizard from './pages/createEndeavors/CreateGoalWizard';
import CreateEventPage from './pages/CreateEventPage';
import CreateAssignmentPage from './pages/CreateAssignmentPage';
import PaymentsPage from './pages/PaymentsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import BillingStatusPage from './pages/BillingStatusPage';
import StudentProgress from './pages/StudentProgress';
import { STUDY_GUIDE_ENABLED } from './config/featureFlags';

// PUBLIC PAGES
import PrivacyPage from './pages/signin/PrivacyPage';
import TermsOfService from './pages/signin/TermsOfService';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogArticle from './pages/BlogArticle';
import DocumentationPage from './pages/docs/DocumentationPage';

// Settings components - ALL EXISTING ONES
import UserList from './pages/settings/UserList';
import CreateUserPage from './pages/settings/CreateUserPage';
import RoleList from './pages/settings/RoleList';
import CreateRolePage from './pages/settings/CreateRolePage';
import OrganizationList from './pages/settings/OrganizationList';
import CreateOrganizationPage from './pages/settings/CreateOrganizationPage';
import UserRolesPage from './pages/settings/UserRolesPage';
import EndeavorTypeEdit from './pages/settings/EndeavorTypeEdit';
import EndeavorTypeList from './pages/settings/EndeavorTypeList';
import EndeavorTypeMetadataList from './pages/settings/EndeavorTypeMetadataList';
import EndeavorTypeMetadataEdit from './pages/settings/EndeavorTypeMetadataEdit';
import DocumentTypeList from './pages/settings/DocumentTypeList';
import DocumentTypeEdit from './pages/settings/DocumentTypeEdit';
import AssignFieldsToDocumentType from './pages/settings/AssignFieldsToDocumentType';
import DocumentMetadataDefinitionList from './pages/settings/DocumentMetadataDefinitionList';
import DocumentMetadataDefinitionEdit from './pages/settings/DocumentMetadataDefinitionEdit';
import EndeavorMetadataDefinitionList from './pages/settings/EndeavorMetadataDefinitionList';
import EndeavorMetadataDefinitionEdit from './pages/settings/EndeavorMetadataDefinitionEdit';
import EventTypeList from './pages/settings/EventTypeList';
import CreateEditEventType from './pages/settings/CreateEditEventType';
import EventMetadataDefinitionList from './pages/settings/EventMetadataDefinitionList';
import EventMetadataDefinitionEdit from './pages/settings/EventMetadataDefinitionEdit';
import LanguageSettings from './pages/settings/LanguageSettings';
import GptPromptEventsDashboard from './pages/settings/GptPromptEventsDashboard'; // NEW: GPT prompt usage analytics
import useIsMobile from './hooks/useIsMobile';
import MobileRoutes from './mobile/MobileRoutes';
import BrandedLoadingScreen from './components/common/BrandedLoadingScreen';
import InviteUserPage from './pages/settings/InviteUserPage';
import UserProfile from './pages/UserProfile';
import ThemePreview from './pages/ThemePreview';
import PaymentsAdminDashboard from './pages/settings/PaymentsAdminDashboard_NEW';
import EmailList from './pages/settings/EmailList';
import EmailEdit from './pages/settings/EmailEdit';
import UsageEventsDashboard from './pages/settings/UsageEventsDashboard'; // Generic usage events dashboard
import UserUsageEventsDashboard from './pages/settings/UserUsageEventsDashboard_NEW'; // Promoted: enhanced per-user dashboard
import GptCostDashboard from './pages/settings/GptPromptEventsDashboard_NEW'; // Cost efficiency dashboard
import UsageEventsDashboardEnhanced from './pages/settings/UsageEventsDashboard_ENHANCED'; // Enhanced aggregate view
import AggregatedMetricsDashboard from './pages/settings/AggregatedMetricsDashboard'; // Aggregated metrics explorer
import AnalyticsOverview from './pages/settings/AnalyticsOverview'; // NEW: Unified analytics overview
import WebhookMonitor from './pages/settings/WebhookMonitor'; // NEW: Payment webhook monitoring
import DataRetentionSettings from './pages/settings/DataRetentionSettings'; // NEW: Data retention admin
import AdminSystemStats from './pages/settings/AdminSystemStats'; // NEW: System stats / cache observability
import CorrelationDetail from './pages/settings/CorrelationDetail'; // NEW: Correlation ID detail view
import FunnelBuilder from './pages/analytics/FunnelBuilder'; // NEW: Funnel analysis visualization
import RetentionDashboard from './pages/analytics/RetentionDashboard'; // NEW: Cohort retention visualization
import AcquisitionDashboard from './pages/analytics/AcquisitionDashboard'; // NEW: Acquisition analytics visualization
import AdminGrantProAccess from './pages/settings/AdminGrantProAccess'; // NEW: Admin grant complimentary pro access
import OrganizationMembersPage from './pages/settings/OrganizationMembersPage';
import UserOrganizationsPage from './pages/settings/UserOrganizationsPage';
import SessionJourneyVisualization from './pages/settings/SessionJourneyVisualization'; // NEW: Session journey timeline
import LearningIntelligenceDashboard from './pages/settings/LearningIntelligenceDashboard'; // NEW: Phase 4 advanced analytics dashboard
import AnalyticsToolkit from './pages/settings/AnalyticsToolkit'; // NEW: Funnel analysis, cohort retention, journey patterns
import ConversionGoals from './pages/settings/ConversionGoals'; // NEW: Conversion goals management
import AlertSettings from './pages/settings/AlertSettings'; // NEW: Real-time alert rules management
import MLInsightsDashboard from './pages/analytics/MLInsightsDashboard'; // NEW: ML-powered analytics dashboard
import SegmentationDashboard from './pages/analytics/SegmentationDashboard'; // NEW: RFM + K-Means segmentation dashboard
import ErrorInvestigationDashboard from './pages/analytics/ErrorInvestigationDashboard'; // NEW: Error investigation dashboard
import { UsageEventsTracker } from './services/UsageEventsTracker';
import ErrorReportingService from './services/ErrorReportingService';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import { ONBOARDING_V2_ENABLED } from './config/featureFlags';
import OnboardingWizard from './pages/onboarding/OnboardingWizard';

// PARENT PORTAL
import ParentRoute from './parent/components/ParentRoute';
import ParentLayout from './parent/components/ParentLayout';
import ParentOnboarding from './parent/pages/ParentOnboarding';
import ParentOverview from './parent/pages/ParentOverview';
import ParentAssignments from './parent/pages/ParentAssignments';
import ParentCourses from './parent/pages/ParentCourses';
import ParentCourseDetail from './parent/pages/ParentCourseDetail';
import ParentActivity from './parent/pages/ParentActivity';
import ParentReports from './parent/pages/ParentReports';
import ParentAlerts from './parent/pages/ParentAlerts';
import ParentStudents from './parent/pages/ParentStudents';
import ParentSettings from './parent/pages/ParentSettings';
import ParentInviteAccept from './parent/pages/ParentInviteAccept';import CourseInviteAccept from './pages/courses/CourseInviteAccept';import { getDefaultHomeRoute } from './utils/onboardingMode';
import { useData } from './contexts/DataContext';

// TEACHER PORTAL
import TeacherRoute from './teacher/components/TeacherRoute';
import TeacherLayout from './teacher/components/layout/TeacherLayout';
import TeacherDashboard from './teacher/pages/TeacherDashboard';
import PlaceholderPage from './teacher/pages/PlaceholderPage';

// Initialize global error capture (window.onerror, unhandledrejection, console.error intercept)
ErrorReportingService.init();

function AppContent() {
  const { accessToken, isInitialized, user } = useAuth();
  const { preferences, loading: prefsLoading, patchPreferences } = usePreferences();
  const { courses } = useData();
  const location = useLocation();
  const hasCourses = courses && courses.length > 0;
  const homeRoute = accessToken
    ? (getDefaultHomeRoute(preferences, hasCourses) || '/dashboard')
    : '/dashboard';

  // Persist has_courses flag so the correct home route is available immediately
  // on the next login (before DataContext hydrates).
  useEffect(() => {
    if (hasCourses && preferences && !preferences.has_courses) {
      patchPreferences({ has_courses: true });
    }
  }, [hasCourses, preferences, patchPreferences]);
  
  // Diagnostic: log imported component types to catch invalid/undefined imports
  if (process.env.NODE_ENV !== 'production') {
    try {
      console.debug('[AppContent] Imported types:', {
        PublicLayout: typeof PublicLayout,
        AppLayout: typeof AppLayout,
        ProtectedRoute: typeof ProtectedRoute,
        AdminRoute: typeof AdminRoute,
        CreateCourseWizard: typeof CreateCourseWizard,
      });
    } catch (e) {}
  }
  // removed unused isChatRoute variable
  const isMobile = useIsMobile(640);

  // isAdmin unused - moved to specific components that need it

  // Track page navigation and performance (only when authenticated)
  useEffect(() => {
    // Only track if user is logged in
    if (!accessToken || !user) return;
    
    // Track page load performance once on mount
    if (typeof window !== 'undefined' && window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      
      if (loadTime > 0) {
        UsageEventsTracker.recordPerformance('page_load', {
          duration: loadTime,
          url: location.pathname,
          metadata: {
            domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
            domInteractive: timing.domInteractive - timing.navigationStart
          }
        });
      }
    }
  }, [accessToken, user, location.pathname]); // Run when authentication state or route changes

  // Track route changes (only when authenticated) + add breadcrumb for error context
  useEffect(() => {
    // Always add breadcrumb for error context (even if not authenticated)
    ErrorReportingService.addBreadcrumb('navigation', `Navigated to ${location.pathname}`, { path: location.pathname });

    // Only track usage event if user is logged in
    if (!accessToken || !user) return;
    
    const referrer = document.referrer || 'direct';
    UsageEventsTracker.recordPageView(location.pathname, referrer, {
      userId: user?.id || user?.userId
    });
  }, [location.pathname, user?.id, user?.userId, accessToken, user]);

  // Keep a lightweight fetchDocuments so children can request a refresh
  const fetchDocuments = useCallback(() => {
    if (!accessToken) return;
    // Optional: ping to warm caches; no local state needed
    DocumentApi.getDocuments({ page: 0, size: 1 }).catch(() => {});
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) fetchDocuments();
  }, [fetchDocuments, accessToken]);

  // Removed unused handleFileUpload

  // Wait until auth is initialized AND preferences are loaded before rendering
  // routes that depend on onboarding mode (prevents flash-redirect to wrong route)
  if (!isInitialized || (accessToken && prefsLoading)) {
    return <BrandedLoadingScreen />;
  }

  // Sidebar shell is now the default (feature flag removed)

  const routeContent = (
    isMobile ? (
      <MobileRoutes />
    ) : (
      <Routes>
            {/* PUBLIC ROUTES - Wrapped in PublicLayout (always shows public navbar) */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={accessToken ? <Navigate to={homeRoute} replace /> : <LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegistrationPage />} />
              <Route path="/signup/plan" element={<PlanSignupPage />} />
              <Route path="/signup/success" element={<SignupSuccessPage />} />
              <Route path="/signup/cancel" element={<SignupCanceledPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:id" element={<BlogArticle />} />
              <Route path="/docs" element={<DocumentationPage />} />
              <Route path="/docs/:section" element={<DocumentationPage />} />
              <Route path="/docs/:section/:articleId" element={<DocumentationPage />} />
            </Route>

            {/* ONBOARDING - Protected but no sidebar/shell */}
            {ONBOARDING_V2_ENABLED && (
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
            )}

            {/* PARENT PORTAL — real parent-role auth (see ParentRoute) */}
            {/* Child-facing invite acceptance: requires auth, not the parent role. */}
            <Route path="/parent-invite/accept" element={<ProtectedRoute><ParentInviteAccept /></ProtectedRoute>} />
            {/* Student-facing course invite acceptance: requires auth. */}
            <Route path="/course-invite/accept" element={<ProtectedRoute><CourseInviteAccept /></ProtectedRoute>} />
            {/* Onboarding requires a real authenticated session. */}
            <Route path="/parent/onboarding" element={<ProtectedRoute><ParentOnboarding /></ProtectedRoute>} />
            <Route element={<ParentRoute><ParentLayout /></ParentRoute>}>
              <Route path="/parent" element={<Navigate to="/parent/overview" replace />} />
              <Route path="/parent/overview" element={<ParentOverview />} />
              <Route path="/parent/assignments" element={<ParentAssignments />} />
              <Route path="/parent/courses" element={<ParentCourses />} />
              <Route path="/parent/courses/:courseId" element={<ParentCourseDetail />} />
              <Route path="/parent/activity" element={<ParentActivity />} />
              <Route path="/parent/reports" element={<ParentReports />} />
              <Route path="/parent/alerts" element={<ParentAlerts />} />
              <Route path="/parent/students" element={<ParentStudents />} />
              <Route path="/parent/settings" element={<ParentSettings />} />
            </Route>

            {/* TEACHER PORTAL — Protected, own layout shell */}
            <Route element={
              <ProtectedRoute>
                <TeacherRoute>
                  <TeacherLayout />
                </TeacherRoute>
              </ProtectedRoute>
            }>
              <Route path="/teacher" element={<Navigate to="/teacher/dashboard" replace />} />
              <Route path="/teacher/dashboard" element={<TeacherDashboard />} />
              <Route path="/teacher/classes" element={<PlaceholderPage title="Classes" description="Create and manage your classes. (Sprint 2)" />} />
              <Route path="/teacher/assignments" element={<PlaceholderPage title="Assignments" description="Create and manage assignments. (Sprint 3)" />} />
              <Route path="/teacher/assessments" element={<PlaceholderPage title="Assessments" description="Build and manage assessments. (Sprint 5)" />} />
              <Route path="/teacher/grading" element={<PlaceholderPage title="Grading Queue" description="Review and grade submissions. (Sprint 4)" />} />
              <Route path="/teacher/gradebook" element={<PlaceholderPage title="Gradebook" description="View and manage grades. (Sprint 6)" />} />
              <Route path="/teacher/library" element={<PlaceholderPage title="Teacher Library" description="Your reusable resources. (Sprint 7)" />} />
              <Route path="/teacher/calendar" element={<PlaceholderPage title="Calendar" description="Teaching schedule and deadlines. (Sprint 7)" />} />
              <Route path="/teacher/search" element={<PlaceholderPage title="Search" description="Search across your classes. (Sprint 7)" />} />
            </Route>

            {/* APP ROUTES - Wrapped in ProtectedRoute and AppLayout (shows app navbar + AppShell) */}
            <Route element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route path="/dashboard" element={<Dashboard />} />
              {STUDY_GUIDE_ENABLED && <Route path="/my-progress" element={<StudentProgress />} />}
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/document/:id" element={<DocumentView fetchDocuments={fetchDocuments} />} />
              <Route path="/document/:id/edit" element={<DocumentEdit onSave={fetchDocuments} />} />
              {/* Redirect old /chat to new AI Tools page */}
              <Route path="/chat" element={<Navigate to="/ai-tools?tool=chat" replace />} />
              <Route path="/chat-new" element={<Navigate to="/ai-tools?tool=chat" replace />} />
              {/* AI Tools hub - unified page for all AI features */}
              <Route path="/ai-tools" element={<AIToolsPage />} />
              <Route path="/course/:courseId" element={<CourseView />} />
              <Route path="/course-card/:courseId" element={<CourseCardPage />} />
              <Route path="/event-card/:eventId" element={<EventCardPage />} />
              <Route path="/course-chat/:courseId" element={<CourseChatPage />} />
              <Route path="/course-documents/:courseId" element={<CourseDocumentsPage />} />
              <Route path="/course/:courseId/edit" element={<CourseEdit />} />
              <Route path="/course/:courseId/admin" element={<CourseAdminDashboard />} />
              <Route path="/my-classes" element={<AnyAdminRoute><CourseAdminHome /></AnyAdminRoute>} />
              <Route path="/admin/organizations" element={<AnyAdminRoute><MyOrganizationsPage /></AnyAdminRoute>} />
              <Route path="/course/:courseId/resources" element={<CourseResourcesPage />} />
              <Route path="/organization/:organizationId/admin" element={
                <OrgRoleRoute paramName="organizationId">
                  <OrgAdminDashboard />
                </OrgRoleRoute>
              } />
              <Route path="/assignment/:assignmentId" element={<AssignmentView />} />
              <Route path="/assignment/:assignmentId/edit" element={<AssignmentEdit />} />

              {/* ADMIN ROUTES - Require Admin Role */}
              <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
              <Route path="/settings/emails" element={<AdminRoute><EmailList /></AdminRoute>} />
              <Route path="/settings/emails/create" element={<AdminRoute><EmailEdit /></AdminRoute>} />
              <Route path="/settings/emails/:id" element={<AdminRoute><EmailEdit /></AdminRoute>} />
              <Route path="/settings/payments-dashboard" element={<AdminRoute><PaymentsAdminDashboard /></AdminRoute>} />
              <Route path="/settings/payments-analytics" element={<AdminRoute><PaymentsAdminDashboard /></AdminRoute>} />
              <Route path="/settings/reminder-policies" element={<AdminRoute><ReminderPolicyPage /></AdminRoute>} />

              {/* DOCUMENT TYPE MANAGEMENT */}
              <Route path="/settings/document-types" element={<AdminRoute><DocumentTypeList /></AdminRoute>} />
              <Route path="/settings/document-types/create" element={<AdminRoute><DocumentTypeEdit /></AdminRoute>} />
              <Route path="/settings/document-types/:id" element={<AdminRoute><DocumentTypeEdit /></AdminRoute>} />
              <Route path="/settings/document-types/:id/assign-fields" element={<AdminRoute><AssignFieldsToDocumentType /></AdminRoute>} />

              {/* DOCUMENT METADATA DEFINITIONS */}
              <Route path="/settings/document-metadata-definitions" element={<AdminRoute><DocumentMetadataDefinitionList /></AdminRoute>} />
              <Route path="/settings/document-metadata-definitions/create" element={<AdminRoute><DocumentMetadataDefinitionEdit /></AdminRoute>} />
              <Route path="/settings/document-metadata-definitions/:id" element={<AdminRoute><DocumentMetadataDefinitionEdit /></AdminRoute>} />

              {/* ENDEAVOR TYPE MANAGEMENT */}
              <Route path="/settings/endeavor-types" element={<AdminRoute><EndeavorTypeList /></AdminRoute>} />
              <Route path="/settings/endeavor-types/create" element={<AdminRoute><EndeavorTypeEdit /></AdminRoute>} />
              <Route path="/settings/endeavor-types/:id/edit" element={<AdminRoute><EndeavorTypeEdit /></AdminRoute>} />
              <Route path="/settings/endeavor-types/:id/metadata" element={<AdminRoute><EndeavorTypeMetadataList /></AdminRoute>} />
              <Route path="/settings/endeavor-types/:id/metadata/create" element={<AdminRoute><EndeavorTypeMetadataEdit /></AdminRoute>} />
              <Route path="/settings/endeavor-types/:id/metadata/:metadataId/edit" element={<AdminRoute><EndeavorTypeMetadataEdit /></AdminRoute>} />

              {/* ENDEAVOR METADATA DEFINITIONS */}
              <Route path="/settings/endeavor-metadata-definitions" element={<AdminRoute><EndeavorMetadataDefinitionList /></AdminRoute>} />
              <Route path="/settings/endeavor-metadata-definitions/create" element={<AdminRoute><EndeavorMetadataDefinitionEdit /></AdminRoute>} />
              <Route path="/settings/endeavor-metadata-definitions/:id" element={<AdminRoute><EndeavorMetadataDefinitionEdit /></AdminRoute>} />

              {/* EVENT TYPE MANAGEMENT - FIXED ROUTES */}
              <Route path="/settings/event-types" element={<AdminRoute><EventTypeList /></AdminRoute>} />
              <Route path="/settings/event-types/create" element={<AdminRoute><CreateEditEventType /></AdminRoute>} />
              <Route path="/settings/event-types/edit/:id" element={<AdminRoute><CreateEditEventType /></AdminRoute>} />

              {/* EVENT METADATA DEFINITIONS */}
              <Route path="/settings/event-metadata-definitions" element={<AdminRoute><EventMetadataDefinitionList /></AdminRoute>} />
              <Route path="/settings/event-metadata-definitions/create" element={<AdminRoute><EventMetadataDefinitionEdit /></AdminRoute>} />
              <Route path="/settings/event-metadata-definitions/:id" element={<AdminRoute><EventMetadataDefinitionEdit /></AdminRoute>} />

              {/* USER MANAGEMENT */}
              <Route path="/settings/users" element={<AdminRoute><UserList /></AdminRoute>} />
              <Route path="/settings/users/create" element={<AdminRoute><CreateUserPage /></AdminRoute>} />
              <Route path="/settings/users/invite" element={<AdminRoute><InviteUserPage /></AdminRoute>} />
              <Route path="/settings/users/:userId" element={<AdminRoute><CreateUserPage /></AdminRoute>} />
              <Route path="/settings/users/:userId/roles" element={<AdminRoute><UserRolesPage /></AdminRoute>} />
              <Route path="/settings/users/:userId/organizations" element={<AdminRoute><UserOrganizationsPage /></AdminRoute>} />

              {/* ROLE MANAGEMENT */}
              <Route path="/settings/roles" element={<AdminRoute><RoleList /></AdminRoute>} />
              <Route path="/settings/roles/create" element={<AdminRoute><CreateRolePage /></AdminRoute>} />
              <Route path="/settings/roles/:roleId" element={<AdminRoute><CreateRolePage /></AdminRoute>} />

              {/* ORGANIZATION MANAGEMENT */}
              <Route path="/settings/organizations" element={<AdminRoute><OrganizationList /></AdminRoute>} />
              <Route path="/settings/organizations/create" element={<AdminRoute><CreateOrganizationPage /></AdminRoute>} />
              <Route path="/settings/organizations/:organizationId" element={<AdminRoute><CreateOrganizationPage /></AdminRoute>} />
              <Route path="/settings/organizations/:organizationId/members" element={<AdminRoute><OrganizationMembersPage /></AdminRoute>} />

              {/* GENERAL SETTINGS */}
              <Route path="/settings/language" element={<AdminRoute><LanguageSettings /></AdminRoute>} />
              <Route path="/settings/analytics-overview" element={<AdminRoute><AnalyticsOverview /></AdminRoute>} />
              <Route path="/settings/gpt-prompt-events" element={<AdminRoute><GptPromptEventsDashboard /></AdminRoute>} />
              <Route path="/settings/gpt-cost-analysis" element={<AdminRoute><GptCostDashboard /></AdminRoute>} />
              <Route path="/settings/usage-events" element={<AdminRoute><UsageEventsDashboard /></AdminRoute>} />
              <Route path="/settings/usage-events-enhanced" element={<AdminRoute><UsageEventsDashboardEnhanced /></AdminRoute>} />
              <Route path="/settings/usage-events/user/:userId" element={<AdminRoute><UserUsageEventsDashboard /></AdminRoute>} />
              <Route path="/settings/aggregated-metrics" element={<AdminRoute><AggregatedMetricsDashboard /></AdminRoute>} />
              <Route path="/settings/session-journey/:sessionId" element={<AdminRoute><SessionJourneyVisualization /></AdminRoute>} />
              <Route path="/settings/webhook-monitor" element={<AdminRoute><WebhookMonitor /></AdminRoute>} />
              <Route path="/settings/data-retention" element={<AdminRoute><DataRetentionSettings /></AdminRoute>} />
              <Route path="/settings/system-stats" element={<AdminRoute><AdminSystemStats /></AdminRoute>} />
              <Route path="/settings/correlation/:correlationId" element={<AdminRoute><CorrelationDetail /></AdminRoute>} />
              <Route path="/settings/grant-pro-access" element={<AdminRoute><AdminGrantProAccess /></AdminRoute>} />
              
              {/* ANALYTICS */}
              <Route path="/analytics/funnel-builder" element={<AdminRoute><FunnelBuilder /></AdminRoute>} />
              <Route path="/analytics/retention" element={<AdminRoute><RetentionDashboard /></AdminRoute>} />
              <Route path="/analytics/acquisition" element={<AdminRoute><AcquisitionDashboard /></AdminRoute>} />
              <Route path="/analytics/learning-intelligence" element={<AdminRoute><LearningIntelligenceDashboard /></AdminRoute>} />
              <Route path="/analytics/toolkit" element={<AdminRoute><AnalyticsToolkit /></AdminRoute>} />
              <Route path="/analytics/conversion-goals" element={<AdminRoute><ConversionGoals /></AdminRoute>} />
              <Route path="/settings/alerts" element={<AdminRoute><AlertSettings /></AdminRoute>} />
              <Route path="/analytics/ml-insights" element={<AdminRoute><MLInsightsDashboard /></AdminRoute>} />
              <Route path="/analytics/segmentation" element={<AdminRoute><SegmentationDashboard /></AdminRoute>} />
              <Route path="/analytics/errors" element={<AdminRoute><ErrorInvestigationDashboard /></AdminRoute>} />
              
              {/* Course wizard, event, and assignment routes */}
              <Route path="/create-course-wizard" element={<CreateCourseWizard />} />
              <Route path="/create-goal-wizard" element={<CreateGoalWizard />} />
              <Route path="/create-event" element={<CreateEventPage />} />
              <Route path="/create-assignment" element={<CreateAssignmentPage />} />
              <Route path="/m/create-assignment" element={<CreateAssignmentPage />} />

              {/* Event view and edit routes */}
              <Route path="/event/:eventId" element={<EventView />} />
              <Route path="/event/:eventId/edit" element={<EventEdit />} />

              {/* USER PROFILE */}
              <Route path="/profile" element={<UserProfile />} />
              {STUDY_GUIDE_ENABLED && <Route path="/my-progress" element={<StudentProgress />} />}
              <Route path="/payments" element={<PaymentsPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/billing" element={<BillingStatusPage />} />
              <Route path="/theme-preview" element={<ThemePreview />} />
            </Route>

            {/* CATCH-ALL ROUTE */}
            <Route path="*" element={
              accessToken ? <Navigate to={homeRoute} replace /> : <Navigate to="/" replace />
            } />
      </Routes>
    )
  );

  // Render routes directly - PublicLayout and AppLayout handle their own structure
  return (
    <>
      {routeContent}
    </>
  );
}

// Main App component that provides the necessary context providers
function App() {
  // AuthProvider already applied in index.js; avoid double mounting auth context
  return (
    <GlobalErrorBoundary>
      <Router>
        <DataProvider>
          <AudioProvider>
            <ThemeProvider>
                <ToastProvider>
                  <AppContent />
                  <ToastContainer />
                </ToastProvider>
            </ThemeProvider>
            <AudioPlayer />
          </AudioProvider>
        </DataProvider>
      </Router>
    </GlobalErrorBoundary>
  );
}

export default App;
